import * as React from "react";
import type { ReadingSlide } from "../ui/StartReader";
import { C, Frame, MONO } from "../ui/readingArt";
import { tx, txv, txr } from "../lib/tx";

/**
 * "Read while you wait" summary for the spot-phishing start screen. Three short
 * slides (what/who and why a human is the control, what to watch for, key terms)
 * shown in the reader while the 3D room downloads. Content only; the reader UI
 * lives in ui/StartReader.tsx. Animation classes (sr-anim-*) are defined in
 * ui/startReader.css. Where an element needs both a position and an animation,
 * the position sits on an outer <g> and the class on an inner one, because the
 * CSS animation transform would replace a transform attribute on the same node.
 */

/* ------------------------------------------------------------------ */
/* 1. a phishing email card in an inbox, with the three possible moves  */
/* ------------------------------------------------------------------ */
function PhishEmailArt(): JSX.Element {
  const H = 296;
  const chips: { x: number; label: string; tone: string; active: boolean }[] = [
    { x: 16.5, label: "Report", tone: C.mint, active: true },
    { x: 130, label: "Verify", tone: C.soft, active: false },
    { x: 243.5, label: "Delete", tone: C.soft, active: false },
  ];
  return (
    <Frame label={tx("Example: a message in your inbox")} aria={tx("An email that claims your password expires today, with a sender address that looks slightly off and a button to keep your password. Below it, three choices: report, verify or delete.")} height={H}>
      <text x="16" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Inbox")}
      </text>
      <rect x="290" y="38" width="54" height="20" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="317" y="52" fontSize="10.5" fontWeight="700" fill={C.muted} textAnchor="middle">
        {tx("1 new")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <rect x="16.5" y="80.5" width="327" height="152" rx="8" fill={C.panel2} stroke={C.line} />
      <circle cx="36" cy="102" r="11" fill={C.amber} fillOpacity="0.14" />
      <text x="36" y="106" fontSize="10.5" fontWeight="700" fill={C.amber} textAnchor="middle">
        {tx("IT")}
      </text>
      <text x="54" y="99" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("IT Help Desk")}
      </text>
      <rect className="sr-anim-pulse" x="52" y="104" width="190" height="16" rx="4" fill={C.red} fillOpacity="0.1" stroke={C.red} strokeOpacity="0.5" style={{ transformOrigin: "147px 112px" }} />
      <text x="58" y="115.5" fontSize="10.5" fontFamily={MONO} fill={C.red}>
        support@northbay-helpdesk.co
      </text>
      <circle className="sr-anim-ring" cx="326" cy="100" r="5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="326" cy="100" r="1.8" fill={C.amber} />

      <line x1="28" y1="130.5" x2="332" y2="130.5" stroke={C.line} />
      <text x="28" y="148" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Your password expires today")}
      </text>
      <text x="28" y="165" fontSize="11" fill={C.soft}>
        {tx("Sign in within 1 hour or your account will be")}
      </text>
      <text x="28" y="179" fontSize="11" fill={C.soft}>
        {tx("locked.")}
      </text>
      <g transform="translate(28 189)">
        <g className="sr-anim-float">
          <rect width="136" height="28" rx="7" fill={C.amber} />
          <text x="68" y="18.5" fontSize="12" fontWeight="700" fill={C.code} textAnchor="middle">
            {tx("Keep my password")}
          </text>
        </g>
      </g>

      <text x="16" y="250" fontSize="10.5" fill={C.muted}>
        {tx("You are the control. Pick one:")}
      </text>
      {chips.map((c) => (
        <g key={c.label}>
          <rect
            className={c.active ? "sr-anim-pulse" : undefined}
            x={c.x}
            y="257"
            width="100"
            height="28"
            rx="14"
            fill={c.active ? C.mint : C.panel2}
            fillOpacity={c.active ? 0.14 : 1}
            stroke={c.active ? C.mint : C.line}
            strokeOpacity={c.active ? 0.5 : 1}
            style={c.active ? { transformOrigin: `${c.x + 50}px 271px` } : undefined}
          />
          <text x={c.x + 50} y="275" fontSize="12" fontWeight="700" fill={c.tone} textAnchor="middle">
            {txv(c.label)}
          </text>
        </g>
      ))}
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. hover-the-link reveal + lookalike domains                         */
/* ------------------------------------------------------------------ */
function Cursor({ x, y }: { x: number; y: number }): JSX.Element {
  return (
    <g transform={`translate(${x} ${y})`}>
      <g className="sr-anim-float">
        <path d="M0 0 L0 15 L4 11.5 L7 18 L9.5 17 L6.5 10.5 L11.5 10.5 Z" fill={C.text} stroke={C.code} strokeWidth="1" strokeLinejoin="round" />
      </g>
    </g>
  );
}

function DomainRow({ y, ok, children, note }: { y: number; ok: boolean; children: React.ReactNode; note: string }): JSX.Element {
  const tone = ok ? C.mint : C.red;
  return (
    <g>
      <rect x="16.5" y={y} width="327" height="28" rx="8" fill={C.panel2} stroke={C.line} />
      <circle cx="33" cy={y + 14} r="7.5" fill={tone} fillOpacity="0.16" />
      {ok ? (
        <polyline points={`29.5,${y + 14} 32,${y + 16.6} 36.5,${y + 11.2}`} fill="none" stroke={tone} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <g stroke={tone} strokeWidth="1.7" strokeLinecap="round">
          <line x1="30" y1={y + 11} x2="36" y2={y + 17} />
          <line x1="36" y1={y + 11} x2="30" y2={y + 17} />
        </g>
      )}
      <text x="48" y={y + 18} fontSize="11.5" fontFamily={MONO} fill={C.soft}>
        {txv(children)}
      </text>
      <text x="334" y={y + 18} fontSize="10.5" fill={C.muted} textAnchor="end">
        {txv(note)}
      </text>
    </g>
  );
}

function HoverLinkArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label={tx("Example: hover before you click")} aria={tx("An email link reading Open invoice, with a pop-up from hovering that reveals a different web address. Below, three domains: the real one, one with a zero instead of an o, and one whose true owner is at the end.")} height={H}>
      <text x="16" y="55" fontSize="12" fill={C.soft}>
        {tx("Hello, your invoice is ready.")}
      </text>
      <text x="16" y="73" fontSize="12" fill={C.soft}>
        {tx("View it here:")}
      </text>
      <text x="96" y="73" fontSize="12" fontWeight="700" fill={C.mint}>
        {tx("Open invoice INV-2291")}
      </text>
      <line x1="96" y1="76.5" x2="236" y2="76.5" stroke={C.mint} strokeWidth="1" />
      <Cursor x={196} y={66} />

      <rect className="sr-anim-pulse" x="16.5" y="94.5" width="327" height="46" rx="8" fill={C.code} stroke={C.red} strokeOpacity="0.6" style={{ transformOrigin: "180px 117px" }} />
      <text x="29" y="111" fontSize="10.5" fill={C.muted}>
        {tx("Hovering shows where it really goes")}
      </text>
      <text x="29" y="129" fontSize="11" fontFamily={MONO} fill={C.red}>
        https://northbay-secure.verify-id.top/in
      </text>

      <line x1="16" y1="156.5" x2="344" y2="156.5" stroke={C.line} />
      <text x="16" y="177" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Lookalike addresses")}
      </text>
      <DomainRow y={188} ok note={tx("the real one")}>
        northbay.com
      </DomainRow>
      <DomainRow y={222} ok={false} note={tx("zero, not o")}>
        {txr("n{tspan}rthbay.com", { tspan: <tspan fill={C.red} fontWeight="700">0</tspan> })}</DomainRow>
      <DomainRow y={256} ok={false} note={tx("the end counts")}>
        {txr("northbay.com.<0>verify-id.top</0>", {}, [<tspan fill={C.red} fontWeight="700" />])}
      </DomainRow>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. red-flag chips                                                    */
/* ------------------------------------------------------------------ */
type Flag = "urgency" | "sender" | "link" | "file" | "login" | "personal";

function FlagGlyph({ g }: { g: Flag }): JSX.Element {
  const s = { fill: "none", stroke: C.red, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "urgency":
      return (
        <g {...s}>
          <circle r="7.5" />
          <polyline points="0,-4 0,0 3.4,2" />
        </g>
      );
    case "sender":
      return (
        <g {...s}>
          <circle cx="0" cy="-3.2" r="3.2" />
          <path d="M-6.5 7 a6.5 5.2 0 0 1 13 0" />
        </g>
      );
    case "link":
      return (
        <g {...s}>
          <rect x="-9.5" y="-3.5" width="10" height="7" rx="3.5" />
          <rect x="-0.5" y="-3.5" width="10" height="7" rx="3.5" />
        </g>
      );
    case "file":
      return (
        <g {...s}>
          <path d="M-5.5 -8 h7 l4 4 v12 h-11 z" />
          <polyline points="1.5,-8 1.5,-4 5.5,-4" />
        </g>
      );
    case "login":
      return (
        <g {...s}>
          <rect x="-6.5" y="-1" width="13" height="9" rx="2" />
          <path d="M-3.5 -1 v-3 a3.5 3.5 0 0 1 7 0 v3" />
        </g>
      );
    case "personal":
      return (
        <g {...s}>
          <circle r="7.5" />
          <circle r="3" />
          <circle r="0.6" />
        </g>
      );
  }
}

const FLAGS: { g: Flag; title: string; sub: string }[] = [
  { g: "urgency", title: "Urgency", sub: "act now or else" },
  { g: "sender", title: "Odd sender", sub: "address is off" },
  { g: "link", title: "Lookalike link", sub: "hover first" },
  { g: "file", title: "Odd attachment", sub: "not expected" },
  { g: "login", title: "Login request", sub: "password or code" },
  { g: "personal", title: "Too personal", sub: "knows too much" },
];

function RedFlagsArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label={tx("Red flags at a glance")} aria={tx("Six red flag chips: urgency, odd sender, lookalike link, odd attachment, login request and too personal. Below them, the advice to pause and verify another way.")} height={H}>
      <text x="16" y="55" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Any one is a reason to pause")}
      </text>
      {FLAGS.map((f, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = 16 + col * 172;
        const y = 70 + row * 62;
        return (
          <g key={f.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width="155" height="51" rx="10" fill={C.panel2} stroke={C.line} />
            <circle cx="26" cy="26" r="16" fill={C.red} fillOpacity="0.12" />
            {i === 0 ? <circle className="sr-anim-ring" cx="26" cy="26" r="16" fill="none" stroke={C.red} strokeWidth="1.5" /> : null}
            <g transform="translate(26 26)">
              <FlagGlyph g={f.g} />
            </g>
            <text x="51" y="23" fontSize="12" fontWeight="700" fill={C.text}>
              {txv(f.title)}
            </text>
            <text x="51" y="39" fontSize="10.5" fill={C.muted}>
              {txv(f.sub)}
            </text>
          </g>
        );
      })}
      <rect x="16.5" y="258.5" width="327" height="28" rx="8" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.4" />
      <text x="30" y="276.5" fontSize="12" fontWeight="600" fill={C.mint}>
        {tx("Pause. Verify another way.")}
      </text>
      <rect className="sr-anim-blink" x="172" y="267" width="1.5" height="12" rx="0.75" fill={C.mint} />
    </Frame>
  );
}

export const SPOT_PHISHING_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "One email can be the way in.",
    illustration: PhishEmailArt,
    caption: "It looks routine. You decide what happens next.",
    sections: [
      {
        h: "What this is about",
        body: "Phishing is a message that tricks you into clicking, signing in or handing something over. It can arrive as an email, a text or a phone call. You learn to spot one, practise on an inbox, and see how spear phishing uses details from public posts to sound believable.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who uses email at work. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Filters stop a lot, but a well-made lure still reaches inboxes. It looks ordinary and asks for something small. Whether to click, reply or report is a human call. Yours. A quick report also protects the colleagues who got the same message.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "Check the sender, the link and the ask.",
    illustration: HoverLinkArt,
    caption: "The real address shows when you hover.",
    sections: [
      {
        h: "Pressure and urgency",
        body: "\u201CToday\u201D, \u201Cfinal notice\u201D and \u201Caccount locked\u201D are built to make you act before you think. Slow down.",
      },
      {
        h: "A sender that is almost right",
        body: "A display name can say anything. Read the full address, and look for small changes: a zero for an o, an extra word, a different ending.",
      },
      {
        h: "Links that hide where they go",
        body: "Hover on a computer, or press and hold on a phone. The owner is the end of the web name, just before the first single slash. Anything before it is decoration.",
      },
      {
        h: "Login pages, files and calls",
        body: "A page that asks for your password or a code after a link is a trap, and so is an unexpected attachment. Attackers also phone, quoting details you posted online, to sound genuine.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: RedFlagsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Phishing", body: "A message that pretends to be someone you trust, to make you click, sign in or share something." },
      { h: "Spear phishing", body: "Phishing aimed at one person, written with real details about their job, team or life." },
      { h: "Voice phishing", body: "The same trick over the phone, often after an email, using urgency and a confident tone." },
      { h: "Lookalike domain", body: "A web address that is almost the real one: a swapped letter, or the real name placed in front of the attacker\u2019s own." },
      { h: "Fake login page", body: "A copy of a sign-in page that collects what you type. The page can look perfect; the address is what gives it away." },
      { h: "Red flag", body: "A sign that a message is not what it claims: urgency, an odd sender, a strange link, a request to sign in." },
      { h: "Verify another way", body: "Check through a channel you already trust, such as a saved number or the real site typed by hand. Never use the contact details in the message." },
    ],
  },
];
